import { useDevCycleClient } from '@devcycle/react-client-sdk';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import type { FeatureFlagConfig, FeatureFlagValueForConfig } from '@/featureFlags/registry';

const isMultivariateVariant = <C extends Extract<FeatureFlagConfig, { type: 'multivariate' }>>(
  config: C,
  value: unknown,
): value is C['variants'][number] =>
  typeof value === 'string' && (config.variants as readonly string[]).includes(value);

const resolveRemoteValue = <C extends FeatureFlagConfig>(
  config: C,
  remoteValue: unknown,
): FeatureFlagValueForConfig<C> => {
  if (config.type === 'multivariate') {
    if (isMultivariateVariant(config, remoteValue)) {
      return remoteValue as FeatureFlagValueForConfig<C>;
    }
    return config.defaultValue as FeatureFlagValueForConfig<C>;
  }

  return (remoteValue ?? config.defaultValue) as FeatureFlagValueForConfig<C>;
};

const useDevCycleRawValue = (config: FeatureFlagConfig): unknown => {
  const client = useDevCycleClient();
  const isDevCycleProvider = config.provider === 'devcycle';
  const [value, setValue] = useState<unknown>(() =>
    isDevCycleProvider ? client.variable(config.key, config.defaultValue).value : undefined,
  );

  useEffect(() => {
    if (!isDevCycleProvider) {
      return;
    }

    const onUpdate = () => {
      setValue(client.variable(config.key, config.defaultValue).value);
    };

    client.subscribe(`variableUpdated:${config.key}`, onUpdate);
    return () => {
      client.unsubscribe(`variableUpdated:${config.key}`, onUpdate);
    };
  }, [client, config.defaultValue, config.key, isDevCycleProvider]);

  return value;
};

const usePostHogRawValue = (config: FeatureFlagConfig): unknown => {
  const client = usePostHog();
  const isPostHogProvider = config.provider === 'posthog';
  const [value, setValue] = useState<unknown>(() =>
    isPostHogProvider ? client.getFeatureFlag(config.key) : undefined,
  );

  useEffect(() => {
    if (!isPostHogProvider) {
      return;
    }

    return client.onFeatureFlags(() => {
      setValue(client.getFeatureFlag(config.key));
    });
  }, [client, config.key, isPostHogProvider]);

  return value;
};

const useRemoteFeatureFlag = <C extends FeatureFlagConfig>(config: C): FeatureFlagValueForConfig<C> => {
  const devcycleValue = useDevCycleRawValue(config);
  const posthogValue = usePostHogRawValue(config);
  const remoteValue = config.provider === 'devcycle' ? devcycleValue : posthogValue;

  return resolveRemoteValue(config, remoteValue);
};

export default useRemoteFeatureFlag;
